import Fab from '@material-ui/core/Fab';
import { withStyles, createStyles } from '@material-ui/core/styles';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import SwitchCameraIcon from '@material-ui/icons/SwitchCamera';
import classNames from 'classnames';
import stringify from 'json-stringify-safe';
import omit from 'lodash/omit';
import startCase from 'lodash/startCase';
import { observer } from 'mobx-react';
import AccCore from 'opentok-accelerator-core';
import 'opentok-archiving';
import 'opentok-solutions-css';
import React, { useState, useEffect, useRef } from 'react';

import * as BN from 'src/components/general/BrowserNotification';
import Tooltip from 'src/components/general/Tooltip';
import { VIDEO_END_KEY } from 'src/components/general/videoEndDialog/VideoEndDialog';
import { InviteGuestModal } from 'src/components/pages/pageElements/InviteGuestModal';
import SignalConnectionTelemetryDataPanelGroup from 'src/components/pages/pageElements/videoConference/SignalConnectionTelemetryDataPanelGroup';
import { TimerPanel } from 'src/components/pages/pageElements/videoConference/TimerPanel';
import {
  ConnectingMask,
  ShowErrorMask,
  StartCallMask,
} from 'src/components/pages/pageElements/videoConference/components/VideoScreenMasks';
import { VideoStatusPromptCard } from 'src/components/pages/pageElements/videoConference/components/VideoStatusPromptCard';
import {
  Attendee,
  VideoConferenceMeta,
  VideoConferencePublishers,
  VideoSignalConnection,
} from 'src/components/pages/pageElements/videoConference/domain/types';
import { OT_OPTIONS } from 'src/components/pages/pageElements/videoConference/opentok/otOptions';
import { useSWRVisitAutoCancelInfo } from 'src/components/pages/pageElements/videoConference/useSWRVisitAutoCancelInfo';
import { useSetSessionOverlappedAt } from 'src/components/pages/pageElements/videoConference/useSetSessionOverlappedAt';
import { IS_AUTO_CANCELING_VISITS_ENABLED } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { getPreferredFullName, Provider } from 'src/shared/stores/resource';
import { CLINICIAN_EVENT_SUBTYPES } from 'src/shared/util/events';
import logger from 'src/shared/util/logger';
import useRootStore from 'src/stores/useRootStore';
import { OPENTOK_API_KEY } from 'src/util';
import { getPatientAttendee } from 'src/util/users';

interface OpenTokStreamEvent {
  type: 'streamCreated' | 'streamDestroyed';
  stream: {
    connection: {
      connectionId: string;
    };
    /** The name passed in by the publisher when they connected to the OT session. This should be JSON parsable and may include string values for userId and name */
    name?: string;
    streamId: string;
  };
}

interface OpenTokSubscriberEvent {
  reason?: string;
  target: {
    id: string;
  };
}

interface OpenTokPropertyChangedEvent {
  changedProperty: string;
  oldValue: any;
  newValue: any;
}

interface OpenTokSignalEvent {
  data: string;
  from: {
    connectionId: string;
  };
}

interface AccCoreEvent {
  publishers?: VideoConferencePublishers;
  subscribers?: {
    camera?: Record<string, any>;
  };
  meta?: VideoConferenceMeta;
}

interface VideoConferenceProps {
  sessionId: string;
  token: string;
  attendees: Attendee[];
  classes: Record<string, string>;
  enableSharing: boolean;
  guestUrl: string;
  inviteGuestByEmail: (email: string) => Promise<void>;
  invitePeerWithReason: (reason: string) => Promise<void>;
  eventId: string;
  startTime?: number;
}

// Count the number of video input devices that
// we can successfully get a video stream for.
async function getVideoDeviceCount() {
  if (navigator.mediaDevices?.enumerateDevices) {
    // Request default audio and video stream so that the user will be
    // prompted for permission for both of these at the same time,
    // otherwise our subsequent code here will prompt for video and then
    // audio will happen separately when the user joins the call
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      return undefined;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasMediaChecks = devices
      .filter(device => device.kind === 'videoinput')
      .map(device =>
        navigator.mediaDevices
          .getUserMedia({ video: { deviceId: { exact: device.deviceId } } })
          .catch(() => false),
      );

    const hasMediaResults = await Promise.all(hasMediaChecks);
    hasMediaResults.forEach(stream => {
      if (stream && typeof stream !== 'boolean') {
        try {
          stream.getTracks().forEach(track => track.stop());
        } catch (err) {
          console.log(err);
        }
      }
    });
    return hasMediaResults.filter(hasMedia => hasMedia).length;
  }
  return undefined;
}

const VideoConference: React.FC<VideoConferenceProps> = props => {
  const rootStore = useRootStore();

  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(true);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(false);
  const [publishers, setPublishers] = useState<VideoConferencePublishers | undefined>(undefined);
  const [signalConnections, setSignalConnections] = useState<VideoSignalConnection[]>([]);
  const [meta, setMeta] = useState<VideoConferenceMeta | undefined>(undefined);
  const [localAudioEnabled, setLocalAudioEnabled] = useState(true);
  const [localVideoEnabled, setLocalVideoEnabled] = useState(true);
  const [showCameraSwitchButton, setShowCameraSwitchButton] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [completedVideoEndDialog, setCompletedVideoEndDialog] = useState(false);
  const [otherProvidersInVisit, setOtherProvidersInVisit] = useState<Record<string, unknown>>({});
  /*
   * There should normally be only one patient connected to a video visit at any time. However,
   * when a patient suddenly closes or loses connection during a video visit, Opentok doesn’t
   * immediately detect the disconnection — it takes about 30 seconds before the stream is officially
   * removed. If the patient rejoins quickly (before the old stream has been removed), there will be
   * a temporary overlap where two entries appear in the patientsInVisit record, one for the old
   * session and one for the new one.
   */
  const [patientsInVisit, setPatientsInVisit] = useState<Record<string, unknown>>({});

  const otCoreRef = useRef<typeof AccCore | null>(null);
  const videoEndBcRef = useRef<BroadcastChannel>(new BroadcastChannel(VIDEO_END_KEY));

  const isClinicianVisit = CLINICIAN_EVENT_SUBTYPES.includes(
    rootStore.events.event?.subType as (typeof CLINICIAN_EVENT_SUBTYPES)[number],
  );

  const patientAttendee = getPatientAttendee(props.attendees);
  const isPatientInVisit = Object.keys(patientsInVisit).length > 0;

  const { data: visitAutoCancelInfo } = useSWRVisitAutoCancelInfo(props.eventId, {
    pollingInterval: 5000, // Poll every 5 seconds
  });

  const { setSessionOverlappedAt } = useSetSessionOverlappedAt();

  useEffect(() => {
    const initializeVideoConference = async () => {
      const { sessionId, token } = props;

      const options = OT_OPTIONS;

      getVideoDeviceCount().then(count =>
        setShowCameraSwitchButton(count !== undefined && count > 1),
      );

      /*
       * Initializing a new AccCore will call OT.initSession, which calls
       * OT.checkSystemRequirements. If it detects the system doesn't support
       * OpenTok, a message is displayed to the user indicating the system
       * requirements for video to play.
       * Ideally we would display our own design for this messaging, but this
       * works for now, as OpenTok-accelerator-core doesn't provide a great
       * way to access the OT.checkSystemRequirements underneath.
       */
      otCoreRef.current = new AccCore({
        ...options,
        credentials: {
          apiKey: OPENTOK_API_KEY,
          sessionId,
          token,
        },
      });

      [
        'subscribeToCamera',
        'unsubscribeFromCamera',
        'subscribeToScreen',
        'unsubscribeFromScreen',
        'startScreenShare',
        'endScreenShare',
      ].forEach(eventName =>
        otCoreRef.current.on(eventName, event => {
          handleSubscriberEvent(eventName, event);
        }),
      );

      ['streamCreated', 'streamDestroyed'].forEach(eventName =>
        otCoreRef.current.getSession().on(eventName, event => {
          handleStreamBoundaryEvent(eventName, event);
        }),
      );
      otCoreRef.current.getSession().on('streamPropertyChanged', handleStreamPropertyChanged);
      otCoreRef.current.getSession().on('signal', populatePatientTelemetryData);

      otCoreRef.current.connect().then(
        () => {
          setConnected(true);
          setConnecting(false);
        },
        error => {
          setFailed(true);
          setConnecting(false);
          logger.error('Failed to connect to video: ', error.message);
        },
      );

      const handleBeforeUnload = () => {
        if (!completedVideoEndDialog && isClinicianVisit) {
          const { id, subType } = rootStore.events.event ?? {};
          videoEndBcRef.current.postMessage({ id, subType });
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        videoEndBcRef.current.close();
        if (otCoreRef.current) {
          otCoreRef.current.disconnect();
        }
      };
    };

    initializeVideoConference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showBrowserNotification = (message: string) => {
    // The use of the `setActive` function with an updater callback helps prevent stale closures
    // by ensuring the latest value of `active` is used
    setActive(prevValue => {
      if (prevValue) {
        BN.show('Boulder Care Staff App', {
          renotify: true,
          tag: 'BoulderCare:Staff',
          requireInteraction: true,
          body: message,
          icon: '/logoBrowserNotification.png',
          image: '/logoBrowserNotification.png',
        } as any);
      }
      return prevValue;
    });
  };

  const logVideoEvent = (
    eventName: string,
    event: AccCoreEvent | OpenTokSubscriberEvent | OpenTokStreamEvent | OpenTokPropertyChangedEvent,
    details = {},
  ) => {
    const { sessionId } = props;
    const addedDetails = {
      careProviderId: rootStore.auth.user?.id,
      ...details,
    };
    const detailsString = stringify(addedDetails, null, 2);
    const message = `OpenTok event ${eventName} in session ${sessionId}: ${detailsString} full event not logged for PII reasons`;
    logger.info(message);
  };

  /** Handles OpenTok session events on the Accelerator Core object.
   * @see {@link https://github.com/opentok/accelerator-core-js#events|Accelerator Core README: Events}
   * @see {@link https://www.tokbox.com/developer/sdks/js/reference/Session.html#events|Session Events Doc}
   */
  const handleSubscriberEvent = (eventName: string, event: AccCoreEvent) => {
    const { subscribers: eventSubscribers, publishers: eventPublishers, meta: eventMeta } = event;
    const cameraSubscribers = eventSubscribers?.camera;
    if (!cameraSubscribers) {
      logVideoEvent(eventName, event, {
        summary: 'no camera subscribers',
      });
      return;
    }

    Object.keys(cameraSubscribers).forEach(cameraKey => {
      cameraSubscribers[cameraKey].on('videoDisabled', handleVideoDisabled);
      cameraSubscribers[cameraKey].on('videoEnabled', handleVideoEnabled);
      logVideoEvent(eventName, event, { cameraKey });
    });

    setPublishers(eventPublishers);
    setMeta(eventMeta);
  };

  /** Handles the 'videoDisabled' event. Note this is a Subscriber event,
   * which we attach to a Subscriber object when they change in the
   * handleSubscriberEvent function.
   * @see {@link https://www.tokbox.com/developer/sdks/js/reference/Subscriber.html#event:videoDisabled|Subscriber videoDisabled Event Doc}
   */
  const handleVideoDisabled = (event: OpenTokSubscriberEvent) => {
    logVideoEvent('videoDisabled', event, {
      reason: event.reason,
      cameraKey: event.target.id,
    });
    const notificationMessage = 'Someone disabled their video';
    showBrowserNotification(notificationMessage);
  };

  const handleVideoEnabled = () => {
    const notificationMessage = 'Someone enabled their video';
    showBrowserNotification(notificationMessage);
  };

  /** Specifically handles 'streamCreated' or 'streamDestroyed' events.
   * @see {@link https://www.tokbox.com/developer/sdks/js/reference/StreamEvent.html|Stream Event Doc}
   */
  const handleStreamBoundaryEvent = async (eventName: string, event: OpenTokStreamEvent) => {
    logVideoEvent(eventName, event);

    const {
      stream: {
        name,
        connection: { connectionId },
        streamId,
      },
    } = event;
    const parsedStreamName = name ? JSON.parse(name) : null;
    const isStreamUserPatientAttendee =
      !!patientAttendee && patientAttendee.id === parsedStreamName?.userId;

    if (eventName === 'streamCreated' && parsedStreamName) {
      // Patient join
      if (isStreamUserPatientAttendee) {
        logger.info(`Patient has joined the visit for event ${props.eventId}`);
        setPatientsInVisit(prevState => ({
          ...prevState,
          [streamId]: parsedStreamName,
        }));
      }

      // Provider join
      if (!isStreamUserPatientAttendee && parsedStreamName?.userId) {
        setOtherProvidersInVisit(prevState => ({
          ...prevState,
          [streamId]: parsedStreamName,
        }));
      }
    }

    if (eventName === 'streamDestroyed') {
      if (!(streamId in patientsInVisit) && !(streamId in otherProvidersInVisit)) {
        logger.error(
          `Attempted to remove non-existent streamId ${streamId} from session ${props.sessionId}`,
        );
      }

      // remove stream and connection info from signalConnections
      setSignalConnections(prevState => {
        return prevState.filter(connection => {
          return connection.connectionId !== connectionId;
        });
      });

      // Display a browser notification that someone has left the visit
      const notificationMessage = 'Someone left your visit';
      showBrowserNotification(notificationMessage);

      // Patient leave
      if (isStreamUserPatientAttendee) {
        logger.info(`Patient has left the visit for event ${props.eventId}`);
        setPatientsInVisit(prevState => omit(prevState, streamId));
      }

      // Provider leave
      // No longer display the name of providers that have left the visit
      if (!isStreamUserPatientAttendee && parsedStreamName?.userId) {
        setOtherProvidersInVisit(prevState => omit(prevState, streamId));
      }
    }
  };

  useEffect(() => {
    const patientSessionIds = Object.keys(patientsInVisit);
    const otherProviderSessionIds = Object.keys(otherProvidersInVisit);
    logger.info(
      `Streams updated for session ${props.sessionId}. ${
        patientSessionIds.length
      } patient stream(s): ${patientSessionIds.join(', ') || 'none'}, ${
        otherProviderSessionIds.length
      } other provider stream(s): ${otherProviderSessionIds.join(', ') || 'none'}`,
    );
  }, [patientsInVisit, otherProvidersInVisit, props.sessionId]);

  // Set sessionOverlappedAt as soon as patient and this provider are both
  // in the visit at the same time. The provider must be active (in the call),
  // not just connected and loitering on the video join ui.
  // Only PIC (clinical drop-in) events have a VisitAutoCancelEntity row — indicated by
  // visitAutoCancelInfo.autoCancelAt being non-null. Using the server's response as the
  // gate (rather than a client-side subType list) prevents missed recordings when a
  // PIC visit subType isn't in the hardcoded list.
  useEffect(() => {
    if (!visitAutoCancelInfo?.autoCancelAt) {
      return;
    }

    if (!props.eventId) {
      logger.error(
        'No eventId provided to VideoConference component. Not setting sessionOverlappedAt',
      );
      return;
    }

    // Even though it's safe to call setSessionOverlappedAt multiple times without
    // overwriting the initial sessionOverlappedAt date, we skip it if we know that
    // it has already been set. Just to avoid the unnecessary API call.
    if (visitAutoCancelInfo?.hasSessionOverlapped) {
      logger.info(
        `Session overlap has already been recorded for event ${props.eventId}, skipping setting it again`,
      );
      return;
    }

    if (isPatientInVisit && active) {
      logger.info(
        `Patient and Provider are in visit. Setting sessionOverlappedAt for event ${props.eventId}`,
      );
      setSessionOverlappedAt(props.eventId);
    }
  }, [
    active,
    isPatientInVisit,
    props.eventId,
    visitAutoCancelInfo?.autoCancelAt,
    visitAutoCancelInfo?.hasSessionOverlapped,
    setSessionOverlappedAt,
  ]);

  /** Handles any stream property changes. Note that this function creates
   * a summary to give a quick description of the event before logging.
   * @see {@link https://www.tokbox.com/developer/sdks/js/reference/Session.html#event:streamPropertyChanged|Session Events Doc}
   * @see {@link https://www.tokbox.com/developer/sdks/js/reference/StreamPropertyChangedEvent.html|StreamPropertyChangedEvent Doc}
   */
  const handleStreamPropertyChanged = (event: OpenTokPropertyChangedEvent) => {
    let { oldValue, newValue } = event;
    if (event.changedProperty === 'videoDimensions') {
      oldValue = `${event.oldValue.width}x${event.oldValue.height}`;
      newValue = `${event.newValue.width}x${event.newValue.height}`;
    }
    logVideoEvent('streamPropertyChanged', event, {
      summary: `${event.changedProperty}: ${oldValue} -> ${newValue}`,
    });
  };

  const populatePatientTelemetryData = (event: OpenTokSignalEvent) => {
    const {
      data, // the signalData payload, parse this stringified json object
      from: { connectionId: incomingConnectionId }, // used to associate signalData with a specific stream/connection
    } = event;
    const incomingSignalData = JSON.parse(data);

    setSignalConnections(prevState => {
      const foundConnection = prevState.find(
        connection => connection.connectionId === incomingConnectionId,
      );

      if (foundConnection) {
        // connection already exists, so update w/ incomingSignalData
        return prevState.map(connection =>
          // attach signalState to the found connection
          connection.connectionId === incomingConnectionId
            ? { ...connection, signalData: incomingSignalData, lastUpdated: new Date() }
            : connection,
        );
      } else {
        // add stream and connection info to signalConnections
        return [
          ...prevState,
          {
            connectionId: incomingConnectionId,
            streamName: undefined, // Will be set later when stream info is available
            signalData: incomingSignalData,
            lastUpdated: new Date(),
          },
        ];
      }
    });
  };

  const goBack = () => {
    window.history.back();
  };

  const startCall = async () => {
    if (!otCoreRef.current) return;

    const {
      firstName,
      patientFacingDisplayName,
      teamRole = '',
    } = (rootStore.auth.user as Provider) ?? {};
    const nameForVideoVisit = `${patientFacingDisplayName || firstName}, ${startCase(
      teamRole ?? undefined,
    )}`;

    // set earlier since being active starts as soon as the startCall method
    // is invoked. Waiting until after the call started caused odd behavior
    // with the join button.
    setActive(true);
    const { publishers: callPublishers, meta: callMeta } = await otCoreRef.current.startCall({
      name: JSON.stringify({ name: nameForVideoVisit, userId: rootStore.auth.user?.id }),
    });
    // set audio and video in call to pre-chosen values
    otCoreRef.current.toggleLocalVideo(localVideoEnabled);
    otCoreRef.current.toggleLocalAudio(localAudioEnabled);
    setPublishers(callPublishers);
    setMeta(callMeta);
    BN.requestPermission();
  };

  const endCall = () => {
    if (!otCoreRef.current) return;

    try {
      otCoreRef.current.endCall();
    } catch (ex) {
      console.warn('Error ending call:', ex);
    }
    setActive(false);
  };

  const toggleLocalAudio = () => {
    if (!otCoreRef.current) return;

    otCoreRef.current.toggleLocalAudio(!localAudioEnabled);
    setLocalAudioEnabled(oldState => !oldState);
  };

  const toggleLocalVideo = () => {
    if (!otCoreRef.current) return;

    otCoreRef.current.toggleLocalVideo(!localVideoEnabled);
    setLocalVideoEnabled(oldState => !oldState);
  };

  const switchCamera = () => {
    if (publishers?.camera) {
      const { camera } = publishers;
      const publisher = camera[Object.keys(camera)[0]];
      if (publisher?.cycleVideo) {
        try {
          publisher.cycleVideo().catch(err => {
            logger.error(err);
            if (err.stack) {
              logger.error(err.stack);
            }
          });
        } catch (err) {
          logger.error(err);
          if (err.stack) {
            logger.error(err.stack);
          }
        }
      }
    }
  };

  const openInviteModal = () => {
    setShowInviteModal(true);
  };

  const { classes, enableSharing, guestUrl, inviteGuestByEmail, invitePeerWithReason } = props;

  const otherProvidersInVisitNames = Object.values(otherProvidersInVisit).map(({ name }) => name);
  const sharingScreen = meta?.publisher?.screen ?? false;
  const viewingSharedScreen = meta?.subscriber?.screen ?? false;
  const activeCameraSubscribers = meta?.subscriber?.camera ?? 0;
  const screenshareActive = viewingSharedScreen || sharingScreen;

  const patientAttendeeName = patientAttendee ? getPreferredFullName(patientAttendee) : null;

  let subColumns;
  let subRows;
  if (activeCameraSubscribers > 0) {
    // Set a column and row for the subscriber container to ensure all participants stay on screen during the call
    // Between 1 - 3 participants: Just display them in one row
    // For 4+ participants: tile the participants to be arranged as close to a square as possible
    subRows = activeCameraSubscribers < 4 ? 1 : Math.ceil(Math.sqrt(activeCameraSubscribers));
    subColumns = Math.ceil(activeCameraSubscribers / subRows);
  }

  const flagIsAutoCancelingVisitsEnabled = useFeatureFlag(IS_AUTO_CANCELING_VISITS_ENABLED);
  // Show auto-cancel prompt cards only when the server confirms this event has an auto-cancel row.
  const showVideoStatusPromptCards =
    flagIsAutoCancelingVisitsEnabled && !!visitAutoCancelInfo?.autoCancelAt;

  return (
    <div>
      <div className={classes.main}>
        <div id="outerVideoContainer" className={classes.outerVideoContainer}>
          {connecting && <ConnectingMask />}
          {failed && <ShowErrorMask />}
          {connected && active && (
            <SignalConnectionTelemetryDataPanelGroup signalConnections={signalConnections} />
          )}

          {!showVideoStatusPromptCards && (
            <>
              {connected && !active && (
                <StartCallMask
                  start={startCall}
                  goBack={goBack}
                  numStreams={
                    Object.keys(otherProvidersInVisit).length + (isPatientInVisit ? 1 : 0)
                  }
                  patientAttendeeName={patientAttendeeName}
                />
              )}

              {connected && props.eventId && props.startTime && !active && (
                <TimerPanel eventId={props.eventId} startTime={props.startTime as number} />
              )}
            </>
          )}

          {showVideoStatusPromptCards && (
            <VideoStatusPromptCard
              startTime={props.startTime as number}
              isPatientInVisit={isPatientInVisit}
              connected={connected}
              active={active}
              startCall={startCall}
              eventId={props.eventId}
              visitAutoCancelInfo={visitAutoCancelInfo}
            />
          )}

          <div
            id="cameraPublisherContainer"
            className={classNames(classes.videoContainer, {
              [classes.videoContainerHidden]: !active,
              [classes.videoContainerSmall]: !!activeCameraSubscribers || screenshareActive,
            })}
          />
          <div
            id="screenPublisherContainer"
            className={classNames(classes.videoContainer, {
              [classes.videoContainerHidden]: !active || !sharingScreen,
            })}
          />
          <div
            id="cameraSubscriberContainer"
            className={classNames(classes.videoContainer, {
              [classes.videoContainerHidden]: !active || !activeCameraSubscribers,
              [classes.videoContainerSmall]: screenshareActive,
            })}
            style={
              subColumns &&
              subRows && {
                gridTemplateColumns: `repeat(${subColumns}, 1fr)`,
                gridTemplateRows: `${(100 / subRows).toString()}%`,
              }
            }
          />
          <div
            id="screenSubscriberContainer"
            className={classNames(classes.videoContainer, {
              [classes.videoContainerHidden]: !viewingSharedScreen || !active,
            })}
          />
        </div>
        <div
          id="controls"
          className={classNames(classes.controls, { [classes.controlsHidden]: !active })}
        >
          <div className={classes.namesContainer}>
            {!!patientAttendeeName && (
              <div id="patientName" className={classNames(classes.attendeeName)}>
                {`Patient: ${patientAttendeeName}`}
              </div>
            )}
            {otherProvidersInVisitNames.length
              ? otherProvidersInVisitNames.map(name => (
                  <div className={classNames(classes.attendeeName)}>{`Provider: ${name}`}</div>
                ))
              : null}
          </div>
          <div className={classes.leftControls}>
            {showCameraSwitchButton && active && (
              <Fab className={classes.cameraSwitchButton} onClick={switchCamera}>
                <SwitchCameraIcon />
              </Fab>
            )}
          </div>
          <div id="centerControls" className={classes.centerControls}>
            <div
              className={classNames('ots-video-control circle microphone', {
                hidden: !active,
                muted: !localAudioEnabled,
                disabled: !localAudioEnabled,
              })}
              onClick={toggleLocalAudio}
            />
            <div
              className={classNames('ots-video-control circle video', {
                hidden: !active,
                muted: !localVideoEnabled,
                disabled: !localVideoEnabled,
              })}
              onClick={toggleLocalVideo}
            />
            <div
              className={classNames('ots-video-control circle end-call', { hidden: !active })}
              onClick={() => {
                endCall();
                if (isClinicianVisit) {
                  const { id, subType } = rootStore.events.event ?? {};
                  videoEndBcRef.current.postMessage({ id, subType });
                  setCompletedVideoEndDialog(true);
                }
              }}
            />
            {enableSharing && (
              <div>
                <Tooltip title="Invite Guest">
                  <Fab
                    onClick={openInviteModal}
                    className={classNames('ots-video-control circle', classes.inviteGuestButton)}
                  >
                    <PersonAddIcon />
                  </Fab>
                </Tooltip>
              </div>
            )}
          </div>
          <div className={classes.rightControls} />
        </div>
        {showInviteModal && (
          <InviteGuestModal
            eventId={props.eventId}
            inviteGuestByEmail={inviteGuestByEmail}
            invitePeerWithReason={invitePeerWithReason}
            guestUrl={guestUrl}
            onCancel={() => {
              setShowInviteModal(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

const styles = () =>
  createStyles({
    namesContainer: {
      display: 'flex',
      flexDirection: 'column',
      fontSize: 20,
      color: 'white',
    },
    main: {
      position: 'absolute',
      width: '100vw',
      height: '100vh',
      zIndex: 9999,
      top: 0,
      backgroundColor: 'black',
    },
    back: {
      position: 'absolute',
      top: 20,
      left: 20,
    },
    cameraSwitchButton: {
      marginLeft: 20,
      color: 'white',
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
    },
    inviteGuestButton: {
      color: 'white',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
      },
    },
    controls: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto',
      position: 'absolute',
      bottom: 0,
      width: '100%',
      zIndex: 1,
      flexDirection: 'column',
    },
    controlsHidden: {
      display: 'none',
    },
    centerControls: {
      display: 'flex',
    },
    leftControls: {
      display: 'flex',
      flex: 1,
    },
    rightControls: {
      display: 'flex',
      justifyContent: 'flex-end',
      flex: 1,
    },
    outerVideoContainer: {
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    attendeeName: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto',
      bottom: 86,
      width: '100%',
      zIndex: 1,
      fontSize: 20,
      color: 'white',
    },
    videoContainer: {
      width: '100%',
      height: '100%',
      display: 'grid',
    },
    videoContainerSmall: {
      position: 'absolute',
      top: 20,
      right: 20,
      width: 160,
      height: 96,
      border: '1px solid #fcba00',
      zIndex: 2,
    },
    videoContainerHidden: {
      display: 'none',
    },
    '@global': {
      '.ots-video-control': {
        width: 50,
        height: 50,
        margin: '20px 5px !important',
        borderRadius: '50%',
        backgroundPosition: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backgroundRepeat: 'no-repeat',
        cursor: 'pointer',
        transition: 'transform .2s',
      },
      '.ots-video-control:hover': {
        transform: 'scale(1.1)',
      },
      '.ots-video-control.microphone.muted': {
        backgroundColor: 'red',
      },
      '.ots-video-control.video.muted': {
        backgroundColor: 'red',
      },
      '.ots-video-control.end-call': {
        backgroundImage: 'url(https://assets.tokbox.com/solutions/images/icon-hang-up.png)',
        backgroundColor: 'red',
      },
    },
  });

export default withStyles(styles)(observer(VideoConference));
